/**
 * AdminImportStatusPage - 4 import-status cards (Phase 12.5).
 *
 * Shape per SPEC §9 AdminImportStatusPage:
 *   1. Freshdesk: progress (imported/target tickets), enabled badge.
 *   2. Gemini: progress (imported/target tickets + comments) + status badge.
 *   3. Inbound email: unparsed_count + enabled badge.
 *   4. Buyist eCommerce API: last_synced_at + last_error + enabled.
 *
 * Disabled components show a "not configured" note so the admin can see
 * which env var to set without flipping back to the runbook.
 */
import { Alert, Badge, ProgressBar, Spinner } from "@/components/ui";
import { ImportStatusCard } from "@/components/admin/ImportStatusCard";
import { useAdminImportStatus } from "@/hooks/useAdminImportStatus";
import { usePageTitle } from "@/hooks/usePageTitle";

function formatDate(iso: string | null): string {
  if (!iso) return "Never";
  return new Date(iso).toLocaleString();
}

export function AdminImportStatusPage(): JSX.Element {
  usePageTitle("Import status");

  const { data, isLoading, error } = useAdminImportStatus();

  return (
    <div className="mx-auto w-full max-w-6xl">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-neutral-900">
          Import status
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          Freshdesk legacy backfill, Gemini archive, inbound email queue,
          and Buyist eCommerce API brand sync.
        </p>
      </header>

      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      )}

      {error && (
        <Alert variant="error" title="Failed to load import status">
          {error instanceof Error ? error.message : "Unknown error."}
        </Alert>
      )}

      {data && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ImportStatusCard
            title="Freshdesk legacy import"
            enabled={data.freshdesk.enabled}
            notConfiguredNote="FD_LEGACY_DATABASE_URL env var is empty - import service is not configured."
          >
            <p className="text-xs text-neutral-600">
              Tickets: {data.freshdesk.imported_tickets.toLocaleString()} /{" "}
              {data.freshdesk.target_tickets.toLocaleString()}
            </p>
            <div className="mt-2">
              <ProgressBar
                value={percent(
                  data.freshdesk.imported_tickets,
                  data.freshdesk.target_tickets,
                )}
              />
            </div>
            <p className="mt-3 text-xs text-neutral-600">
              Comments:{" "}
              {data.freshdesk.imported_comments.toLocaleString()} /{" "}
              {data.freshdesk.target_comments.toLocaleString()}
            </p>
            <div className="mt-2">
              <ProgressBar
                size="sm"
                value={percent(
                  data.freshdesk.imported_comments,
                  data.freshdesk.target_comments,
                )}
              />
            </div>
          </ImportStatusCard>

          <ImportStatusCard
            title="Gemini archive"
            enabled={data.gemini.enabled}
            enabledBadgeLabel={
              data.gemini.status === "completed" ? "Complete" : "Running"
            }
            disabledBadgeLabel="Not started"
            notConfiguredNote="Gemini archive import has not run yet (SPEC §7)."
          >
            <p className="text-xs text-neutral-600">
              Tickets: {data.gemini.imported_tickets.toLocaleString()} /{" "}
              {data.gemini.target_tickets.toLocaleString()}
            </p>
            <div className="mt-2">
              <ProgressBar
                value={percent(
                  data.gemini.imported_tickets,
                  data.gemini.target_tickets,
                )}
              />
            </div>
            <p className="mt-3 text-xs text-neutral-600">
              Comments:{" "}
              {data.gemini.imported_comments.toLocaleString()} /{" "}
              {data.gemini.target_comments.toLocaleString()}
            </p>
            <div className="mt-2">
              <ProgressBar
                size="sm"
                value={percent(
                  data.gemini.imported_comments,
                  data.gemini.target_comments,
                )}
              />
            </div>
            <p className="mt-3 text-xs text-neutral-600">
              Status:{" "}
              <Badge variant="default">{data.gemini.status}</Badge>
            </p>
          </ImportStatusCard>

          <ImportStatusCard
            title="Inbound email"
            enabled={data.inbound_email.enabled}
            notConfiguredNote="inbound_emails table is not yet present. Phase 8 wires the Microsoft Graph change-notification subscription."
          >
            <p className="text-xs text-neutral-600">
              Unparsed in queue:{" "}
              <span className="font-semibold text-neutral-900">
                {data.inbound_email.unparsed_count.toLocaleString()}
              </span>
            </p>
          </ImportStatusCard>

          <ImportStatusCard
            title="Buyist eCommerce API"
            enabled={data.buyist_api.enabled}
            notConfiguredNote="BUYIST_BRAND_SYNC_ENABLED is false. Set it in env when the Buyist API is reachable."
          >
            <p className="text-xs text-neutral-600">
              Last sync: {formatDate(data.buyist_api.last_synced_at)}
            </p>
            {data.buyist_api.last_error && (
              <p className="mt-2 rounded-md bg-red-50 p-2 text-xs text-red-700">
                Last error: {data.buyist_api.last_error}
              </p>
            )}
          </ImportStatusCard>
        </div>
      )}
    </div>
  );
}

function percent(numerator: number, denominator: number): number {
  if (denominator <= 0) return 0;
  return Math.round((numerator / denominator) * 100);
}

export default AdminImportStatusPage;
